import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, Dumbbell, MapPin } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { UserContext } from '../App';
import { useModal } from '../context/ModalContext';
import MemberCenterLayout from '../components/common/MemberCenterLayout';
import { SPORT_CATEGORY } from '../utils/eventCategories';
import { createMemberRecordTabs, eventMatchesRecordMode } from '../utils/memberRecordTabs';

const TABS = createMemberRecordTabs('upcoming');

const STATUS_BADGE = {
  upcoming: { text: '已確認', bg: '#dcfce7', color: '#16a34a' },
  pending: { text: '審核中', bg: '#fef9c3', color: '#ca8a04' },
  history: { text: '已結束', bg: '#f3f4f6', color: '#6b7280' },
  cancelled: { text: '已取消', bg: '#fef2f2', color: '#b91c1c' },
};

const CATEGORY_COLORS = {
  '登山': '#10b981', '桌遊': '#6366f1', '聚餐': '#f59e0b',
  '展覽': '#8b5cf6', '運動': '#ef4444', '音樂': '#ec4899',
  '旅遊': '#0ea5e9', '其他': '#6b7280',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()} (${['日', '一', '二', '三', '四', '五', '六'][d.getDay()]})`;
}

function StatusBadge({ type }) {
  const badge = STATUS_BADGE[type];
  if (!badge) return null;
  return (
    <span style={{
      fontSize: '0.7rem', fontWeight: '600', padding: '2px 8px',
      borderRadius: '12px', backgroundColor: badge.bg, color: badge.color
    }}>
      {badge.text}
    </span>
  );
}

function EventMiniCard({ event, badgeType, participantCount, maxParticipants, isSport }) {
  const navigate = useNavigate();
  const categoryColor = CATEGORY_COLORS[event.category] || '#6b7280';
  const categoryLabel = isSport ? (event.subcategory || SPORT_CATEGORY) : (event.category || '其他');

  return (
    <button
      type="button"
      className="member-center-surface"
      onClick={() => navigate(`/events/${event.id}`)}
      style={{
        backgroundColor: 'white',
        borderRadius: '20px',
        padding: '16px',
        boxShadow: '0 10px 28px rgba(15,23,42,0.055)',
        cursor: 'pointer',
        border: '1px solid #dbe4ee',
        width: '100%',
        fontFamily: 'inherit',
        textAlign: 'left',
        transition: 'transform 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div style={{ flex: 1, minWidth: 0, marginRight: '8px' }}>
          <div style={{ fontSize: '1rem', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
            {event.title}
          </div>
          <span style={{
            fontSize: '0.7rem', fontWeight: '600', padding: '2px 8px',
            borderRadius: '12px', backgroundColor: `${categoryColor}20`, color: categoryColor
          }}>
            {categoryLabel}
          </span>
        </div>
        {badgeType === 'organized' ? (
          <span style={{
            fontSize: '0.7rem', fontWeight: '600', padding: '2px 8px',
            borderRadius: '12px', backgroundColor: '#ede9fe', color: '#7c3aed',
            whiteSpace: 'nowrap'
          }}>
            {participantCount} / {maxParticipants || '不限'}人
          </span>
        ) : (
          <StatusBadge type={badgeType} />
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '0.8rem' }}>
          <Calendar size={13} />
          <span>{formatDate(event.date)}</span>
        </div>
        {event.location && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '0.8rem' }}>
            <MapPin size={13} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.location}</span>
          </div>
        )}
      </div>
    </button>
  );
}

function EmptyState({ tab, onBrowse, isSport }) {
  const recordName = isSport ? '運動' : '活動';
  const messages = {
    upcoming: { text: `目前沒有即將參加的${recordName}`, sub: '去探索看看吧！', showBtn: true },
    pending: { text: '目前沒有待審核的報名申請', sub: `先去看看有沒有喜歡的${recordName}`, showBtn: true },
    organized: { text: `你還沒有發起任何${recordName}`, sub: `來發起一場${recordName}吧！`, showBtn: false },
    history: { text: '還沒有歷史參加紀錄', sub: `參加幾場${recordName}後就會出現囉`, showBtn: true },
  };
  const m = messages[tab] || messages.upcoming;

  return (
    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '48px 24px' }}>
      {isSport
        ? <Dumbbell size={42} color="#9cc2e6" strokeWidth={1.5} style={{ marginBottom: '12px' }} />
        : <Calendar size={42} color="#9cc2e6" strokeWidth={1.5} style={{ marginBottom: '12px' }} />}
      <div style={{ fontSize: '1rem', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>{m.text}</div>
      <div style={{ fontSize: '0.875rem', color: '#9ca3af', marginBottom: '24px' }}>{m.sub}</div>
      {m.showBtn && (
        <button
          onClick={onBrowse}
          style={{
            padding: '10px 24px', backgroundColor: '#4e8bca', color: 'white',
            border: 'none', borderRadius: '20px', fontSize: '0.875rem',
            fontWeight: '600', cursor: 'pointer'
          }}
        >
          瀏覽{recordName}
        </button>
      )}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="member-center-surface" style={{
      backgroundColor: 'white', borderRadius: '20px', padding: '16px',
      boxShadow: '0 10px 28px rgba(15,23,42,0.055)'
    }}>
      <div style={{ height: '18px', width: '60%', backgroundColor: '#f1f5f9', borderRadius: '6px', marginBottom: '10px' }} />
      <div style={{ height: '14px', width: '30%', backgroundColor: '#f1f5f9', borderRadius: '6px', marginBottom: '8px' }} />
      <div style={{ height: '13px', width: '45%', backgroundColor: '#f1f5f9', borderRadius: '6px' }} />
    </div>
  );
}

export default function MyEvents({ mode = 'event' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useContext(UserContext);
  const isSport = mode === 'sport';
  const [activeTab, setActiveTab] = useState(TABS.some(tab => tab.key === location.state?.tab) ? location.state.tab : 'organized');
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({ upcoming: [], pending: [], organized: [], history: [] });
  const { showAlert } = useModal();

  const loadMyEvents = useCallback(async () => {
    if (!currentUser?.id) return;
    setIsLoading(true);

    try {
      const today = new Date().toISOString().split('T')[0];

      // 查詢我參加的 (approved) + 待審核 (pending)
      const { data: participated, error: e1 } = await supabase
        .from('event_participants')
        .select(`
          status,
          joined_at,
          events (
            id, title, date, location, category, subcategory, max_participants,
            organizer_id, status
          )
        `)
        .eq('user_id', currentUser.id)
        .in('status', ['approved', 'pending'])
        .order('joined_at', { ascending: false });

      if (e1) throw e1;

      // 查詢我發起的活動
      const { data: organized, error: e2 } = await supabase
        .from('events')
        .select(`
          id, title, date, location, category, subcategory, max_participants, created_at, status,
          event_participants (user_id)
        `)
        .eq('organizer_id', currentUser.id)
        .order('date', { ascending: false });

      if (e2) throw e2;

      const upcoming = [];
      const history = [];
      const pending = [];

      (participated || []).forEach(row => {
        if (!row.events) return;
        if (!eventMatchesRecordMode(row.events, mode)) return;
        const eventDate = row.events.date?.split('T')[0] || '';
        if (row.events.status === 'cancelled') {
          history.push(row.events);
        } else if (row.status === 'pending') {
          pending.push(row.events);
        } else if (eventDate >= today) {
          upcoming.push(row.events);
        } else {
          history.push(row.events);
        }
      });

      const activeOrganized = [];
      (organized || []).forEach(event => {
        if (!eventMatchesRecordMode(event, mode)) return;
        if (event.status === 'cancelled') history.push(event);
        else activeOrganized.push(event);
      });

      setData({
        upcoming,
        pending,
        organized: activeOrganized,
        history,
      });
    } catch (error) {
      console.error('載入我的活動失敗:', error);
      showAlert('錯誤', '載入活動資料失敗，請稍後再試。');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, mode, showAlert]);

  useEffect(() => {
    loadMyEvents();
  }, [loadMyEvents]);

  const counts = {
    upcoming: data.upcoming.length,
    pending: data.pending.length,
    organized: data.organized.length,
    history: data.history.length,
  };

  return (
    <MemberCenterLayout
      title={isSport ? '我的運動' : '我的活動'}
      subtitle={`管理你的${isSport ? '運動' : '活動'}報名、發起與歷史紀錄`}
      activeKey={isSport ? 'sports' : 'events'}
    >
        <div className="member-center-tabs" role="tablist" aria-label={`我的${isSport ? '運動' : '活動'}分類`}>
          {TABS.map(tab => {
            const isActive = activeTab === tab.key;
            const count = counts[tab.key];
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.key)}
                className={`member-center-tab${isActive ? ' member-center-tab--active' : ''}`}
              >
                {tab.label}
                {count > 0 && <span className="member-center-tab__count">{count}</span>}
              </button>
            );
          })}
        </div>

        <div className="member-center-card-grid">
          {isLoading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : activeTab === 'upcoming' ? (
            data.upcoming.length === 0
              ? <EmptyState tab="upcoming" isSport={isSport} onBrowse={() => navigate(isSport ? '/sports' : '/events')} />
              : data.upcoming.map(event => (
                <EventMiniCard key={event.id} event={event} badgeType="upcoming" isSport={isSport} />
              ))
          ) : activeTab === 'pending' ? (
            data.pending.length === 0
              ? <EmptyState tab="pending" isSport={isSport} onBrowse={() => navigate(isSport ? '/sports' : '/events')} />
              : data.pending.map(event => (
                <EventMiniCard key={event.id} event={event} badgeType="pending" isSport={isSport} />
              ))
          ) : activeTab === 'organized' ? (
            data.organized.length === 0
              ? <EmptyState tab="organized" isSport={isSport} onBrowse={() => navigate(isSport ? '/sports' : '/events')} />
              : data.organized.map(event => (
                <EventMiniCard
                  key={event.id}
                  event={event}
                  badgeType="organized"
                  participantCount={event.event_participants?.length ?? 0}
                  maxParticipants={event.max_participants}
                  isSport={isSport}
                />
              ))
          ) : activeTab === 'history' ? (
            data.history.length === 0
              ? <EmptyState tab="history" isSport={isSport} onBrowse={() => navigate(isSport ? '/sports' : '/events')} />
              : data.history.map(event => (
                <EventMiniCard key={event.id} event={event} badgeType={event.status === 'cancelled' ? 'cancelled' : 'history'} isSport={isSport} />
              ))
          ) : null}
        </div>
    </MemberCenterLayout>
  );
}
